import { type NetPriceData, type PriceData } from "../../../../models/PriceData";
import { type WeightData } from "../../../../models/WeightData";
import { type StockLevel } from "../../../../common/components/StockIndicator/StockIndicator";
import { type LineItemMessage } from "../../../../utils/cartUpdating/common";
import { type KITProductData } from "../../../../models/ProductData";
import { type StockData } from "~/utils/cartUpdating/delivery/stockDataTypes";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { PurchasePlace, PurchaseType } from "~/feature/Analytics/ga/recommendations/types";

export interface CartEntry {
  pk: number;
  id: number;
  /**
   * @deprecated use productCode at line item level instead
   */
  productCode: string;
  packEntryIds: number[];
  /**
   * @deprecated use cartEntryNumber at line item level instead
   */
  entryNumber: number;
}

export interface GroupedCart {
  orderables: CartGrouping;
  quotables: CartGrouping;
  bundles: BundledQuoteGrouping[];
}

export enum CartGroup {
  ORDER,
  QUOTE,
  BUNDLE,
}

export interface CartGrouping {
  type: CartGroup;
  entries: GroupedCartEntry[];
}

export interface BundledQuoteGrouping extends CartGrouping {
  type: CartGroup.BUNDLE;
  quoteNumber: string;
}

export interface GroupedCartEntry {
  cartEntryId: number;
  packGroups: GroupedPackEntries[];
}

export interface GroupedPackEntries {
  packEntryIds: number[];
}

export enum PackageEntrySource {
  DEFAULT = "DEFAULT",
  BULK = "BULK",
  QUOTE = "QUOTE",
  CONTRACT = "CONTRACT",
}

export interface EstimatedDeliveryDate {
  type: "estimate" | "replenishment";
  date: Date;
}

export type LineItem = PlainLineItem | SubLineItem;

export interface PlainLineItem {
  pk: number;
  id: number;
  productCode: string;
  cartEntryId: number;
  cartEntryPk: number;
  cartEntryNumber: number;
  entryNumber: number | null;
  quantity: number;
  packSize?: number;
  packCode: string;
  deliveryDate: Date | null;
  manuallySetRDD: boolean;
  weight: WeightData;
  comment?: string;
  valid: boolean;
  primaryWarehouseName: string;
  primaryWarehouseCode: string;
  netPrice?: NetPriceData;
  messages: LineItemMessage[];
  contractErrorMessage?: LineItemMessage;
  disableEdit: boolean;
  countryOfOrigin: string;
  tariffSurcharge: boolean;

  // Line item type as provided by BE
  source: PackageEntrySource;

  minRdd?: Date;
  maxRdd?: Date;

  showComments: boolean;
  priceReference: string;

  // Salesman specific fields
  internalComment: string;
  /**
   * This field is an exception - this price is NOT per piece, but per {LineItem#priceUnit} pieces
   */
  offerPrice?: PriceData;
  priceOverriden: boolean;
  targetPrice?: PriceData;
  /**
   * Minimum Sale Price per single piece of product. Even when priceUnit !== 1
   * TODO: Should be price per {priceUnit} pieces
   */
  msp?: PriceData;
  /**
   * Recommended Sale Price per single piece of product. Even when priceUnit !== 1
   * TODO: Should be price per {priceUnit} pieces
   */
  rsp?: PriceData;

  // Contract specific fields
  contractNumber: string;
  contractLine: string;

  // Quote specific fields
  quoteNumber: string;
  quoteLine: string;
  minimumQty: number;
  isBundled?: boolean;
  original: boolean;

  estimatedShippingDate: Date | null;
  logisticalComment?: string;
  quoteExpiryDate: Date | null;

  // Fields not sent to hybris - filled based on Availability data
  edd: EstimatedDeliveryDate | null;
  isInStock: StockLevel | null;

  // UI Fields - also not sent to hybris
  isPackagingSelectionOpen: boolean; // Show packaging selection in Distributor variant

  // Disables the option of changing package
  restrictedPackage: boolean;

  optionalPartIds: number[];

  customerOrderNumber: string;

  partialDeliveries?: StockData;

  // Sales push fields
  saleCampaignCode?: string;
  actualSalePrice?: PriceData;
  actualNetPrice?: PriceData;
  saleCampaignEnabled?: boolean;

  // Product Recommendation fields
  recoPlace?: PurchasePlace;
  recoType?: PurchaseType;

  // Switch to newest product version
  successorProductCode?: string;
}

/**
 * Represents an optional part of a plain line item
 */
export interface SubLineItem extends Omit<PlainLineItem, "optionalPartIds" | "entryId"> {
  parentLineItemId: number;
  optionalPartCode: string;
  tariffSurcharge: boolean;
}

export interface ResolvedCartGrouping extends CartGrouping {
  entries: ResolvedGroupedCartEntry[];
}
export interface ResolvedGroupedCartEntry extends GroupedCartEntry {
  cartEntry: CartEntry;
  descriptors: ProductDescriptor[];
  packGroups: ResolvedGroupedPackEntries[];
}
export interface ResolvedGroupedPackEntries extends GroupedPackEntries {
  packEntries: LineItem[];
}
export interface ResolvedGroupedCartEntryKIT {
  cartEntry: CartEntry;
  productDescriptor: ProductDescriptor<KITProductData>;
  packEntry: LineItem;
}

export function isPlainLineItem(lineItem: LineItem): lineItem is PlainLineItem {
  return "optionalPartIds" in lineItem;
}

export function isSubLineItem(lineItem: LineItem): lineItem is SubLineItem {
  return "parentLineItemId" in lineItem;
}
